import { useState } from 'react';
import loadable from '@loadable/component';
import { useCompanyCanonicalUrl, useSEO } from '@/hooks/useCompany';
import { SectionWrapper } from '../../styled';
import DocumentSchema from '@/components/DocumentSchema';
import type { Corporation } from 'schema-dts';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useLocale } from '@/hooks/useLocale';
import { SWS_HOST } from '@/constants';
import { generateQandA } from '../Valuation/utils';
import { useOverviewTranslation } from './hooks/useOverviewTranslation';
import { getPassRewardList, getRiskList } from './utils';

import { useCompanySummary } from './hooks/useCompanySummary';
import { useUser } from '@/hooks/useUser';

import { useFlag } from '@simplywallst/unleash';
import { PUBLIC_SWS_ENV } from '@/constants/env';
import { isValueInPercentageRange } from '../../utils/isValueInPercentageRange';

const Summary = loadable(() => import('./components/Summary'));

// Used to return peers for ASX companies
const PeerCompanies = loadable(
  () =>
    import('@/pages/CompanyReport/components/SimilarCompanies/PeerCompanies')
);

const Fundamentals = loadable(
  () =>
    import('@/pages/CompanyReport/components/Overview/components/Fundamentals'),
  {
    resolveComponent: (component) => component.Fundamentals,
  }
);

const PricePerformance = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/PricePerformance'
    ),
  { resolveComponent: (components) => components.PricePerformance }
);

const AboutTheCompanySection = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/OverviewSection/components/AboutTheCompanySection'
    )
);

const MyNotes = loadable(
  () => import('@/pages/CompanyReport/components/MyNotes')
);

const AutoCompanyNoteModal = loadable(
  () => import('@/pages/CompanyReport/components/AutoCompanyNoteModal')
);

const CompanyNarratives = loadable(
  () => import('@/pages/Narrative/components/CompanyNarratives'),
  { resolveComponent: (component) => component.CompanyNarratives }
);

const useNarrativeRelocation = () => {
  const flagKillSwitch = useFlag('mono-narratives-relocation-killswitch');
  const canonicalUrl = useCompanyCanonicalUrl();
  const {
    data: { loggedIn },
  } = useUser();

  const isKillSwitchEnabled = flagKillSwitch.enabled;

  // Avoid relocating narratives for logged in users
  if (loggedIn) return false;

  // Relocate narratives based on flagKillSwitch value for non-prod environments
  if (PUBLIC_SWS_ENV !== 'prod') return !isKillSwitchEnabled;

  // Avoid relocating narratives in test environment until we have some tests in place
  if (NODE_ENV === 'test') return false;

  // Prod will always use the percentage range checker to avoid issues with CLS
  return isValueInPercentageRange(canonicalUrl, 50) && !isKillSwitchEnabled;
};

const OverviewSection = () => {
  const [showAboutCompany, setShowAboutCompany] = useState(false);

  const canonicalUrl = useCompanyCanonicalUrl();

  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const overviewT = useOverviewTranslation();
  const locale = useLocale();

  const { description } = useSEO('summary');

  const {
    data: { loggedIn },
  } = useUser();

  const {
    companyId,
    canonicalURL,
    media,
    name,
    shortName,
    yearFunded,
    url,
    exchangeSymbol,
    tickerSymbol,
    uniqueSymbol,
    address,
    address2,
    city,
    state,
    country,
  } = useCompanySummary({ canonicalUrl });

  const localePath = locale === 'en' ? '' : `/${locale}`;
  const canonicalUrlWithHost = `${SWS_HOST}${localePath}${canonicalURL}`;

  const riskList = getRiskList(statements);
  const passRewardsList = getPassRewardList(statements);

  const riskRewardFaqs = passRewardsList.map((passReward) => {
    return generateQandA(
      passReward.title.endsWith('?')
        ? passReward.title
        : `${passReward.title}?`,
      passReward.state === 'pass' ? overviewT('faqYes') : overviewT('faqNo')
    );
  });

  Object.values(riskList).forEach((risks) => {
    risks.forEach((risk) => {
      riskRewardFaqs.push(
        generateQandA(
          risk.title.endsWith('?') ? risk.title : `${risk.title}?`,
          risk.state === 'pass' ? overviewT('faqYes') : overviewT('faqNo')
        )
      );
    });
  });

  const fullAddress =
    [address, address2, city, state, country].filter(Boolean).join(' ') || '';

  const showNarrativesAfterPricePerformance = useNarrativeRelocation();

  return (
    <>
      {canonicalURL && (
        <DocumentSchema<Corporation>
          item={{
            '@context': 'https://schema.org',
            '@type': 'Corporation',
            identifier: canonicalUrlWithHost,
            name: shortName ?? '',
            legalName: name ?? '',
            tickerSymbol: `${exchangeSymbol}:${tickerSymbol}`,
            url: canonicalUrlWithHost,
            logo: media?.[1]?.url ?? '',
            foundingDate: yearFunded?.toString(),
            description,
            sameAs: url ?? '',
            address: fullAddress,
          }}
        />
      )}
      <Summary handleShowAboutCompany={setShowAboutCompany} />
      {canonicalUrl && !showNarrativesAfterPricePerformance && (
        <CompanyNarratives canonicalUrl={canonicalUrl} />
      )}
      {companyId && uniqueSymbol && (
        <>
          {loggedIn && (
            <MyNotes companyId={companyId ?? ''} uniqueSymbol={uniqueSymbol} />
          )}
          <AutoCompanyNoteModal
            companyId={companyId ?? ''}
            uniqueSymbol={uniqueSymbol}
          />
        </>
      )}
      <PeerCompanies
        canonicalURL={canonicalUrl || ''}
        name={name ?? ''}
        section="summary"
      />
      <SectionWrapper data-cy-id="report-section">
        <PricePerformance
          id={name}
          uniqueSymbol={uniqueSymbol || ''}
          lookup={canonicalUrl || ''}
        />
      </SectionWrapper>
      {canonicalUrl && showNarrativesAfterPricePerformance && (
        <CompanyNarratives canonicalUrl={canonicalUrl} />
      )}
      <AboutTheCompanySection
        showAboutCompany={showAboutCompany}
        handleShowAboutCompany={setShowAboutCompany}
      />
      <Fundamentals name={name ?? ''} />
    </>
  );
};

export default OverviewSection;
